import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { CircleNotch, ArrowCounterClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ArchiveDialog({ productId, asset, onClose, onArchived }) {
  const [impact, setImpact] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/evidence/${productId}/${asset.id}/archive-impact`)
      .then((r) => setImpact(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load the impact preview."));
  }, [productId, asset.id]);

  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required to archive evidence."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/evidence/${productId}/${asset.id}/archive`,
        { reason: reason.trim(), confirm: true });
      toast.success(`Archived — recoverable until ${r.data.recovery_deadline.slice(0, 10)}`);
      onArchived();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="archive-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-sm">Archive evidence — impact preview</DialogTitle>
        </DialogHeader>
        {error ? (
          <p data-testid="archive-impact-error" className="text-xs text-red-600 py-4">{error}</p>
        ) : !impact ? (
          <p data-testid="archive-impact-loading" className="text-xs text-slate-400 py-4">
            <CircleNotch size={14} className="animate-spin inline mr-1.5" />Calculating the downstream impact…</p>
        ) : (
          <div data-testid="archive-impact" className="space-y-3 text-xs">
            <div className="bg-slate-50 border border-slate-200 px-3 py-2">
              <p className="font-semibold text-slate-800">{impact.title}</p>
              <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">ID: {impact.asset_id}{impact.design_label && ` · ${impact.design_label}`}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="overline-label text-slate-400">Products affected</p>
                <p className="text-slate-700 mt-0.5">{impact.products_affected.map((p) => p.name).join(", ")}</p>
              </div>
              <div>
                <p className="overline-label text-slate-400">Countries affected</p>
                <p data-testid="archive-countries" className="text-slate-700 mt-0.5">
                  {impact.countries_affected.length} market(s){impact.countries_affected.length > 0 && ` — ${impact.countries_affected.slice(0, 6).map((c) => c.name).join(", ")}${impact.countries_affected.length > 6 ? "…" : ""}`}
                </p>
              </div>
            </div>
            <div>
              <p className="overline-label text-slate-400">Requirements affected</p>
              <p data-testid="archive-requirements" className="text-slate-700 mt-0.5">
                {impact.requirements_affected.length === 0 ? "None" :
                  `${impact.requirements_affected.length} requirement slot(s) across ${new Set(impact.requirements_affected.map((r) => r.country)).size} market(s) — e.g. ${impact.requirements_affected.slice(0, 3).map((r) => `${r.type} (${r.country})`).join("; ")}`}
              </p>
            </div>
            <div data-testid="archive-score-effect" className="bg-amber-50 border border-amber-200 px-3 py-2">
              <p className="overline-label text-amber-700">Current score effect</p>
              <p className="text-amber-800 mt-0.5">
                Provisional GMARI {impact.score_effect.provisional_before} → {impact.score_effect.provisional_after}
                {" "}({impact.score_effect.provisional_delta >= 0 ? "+" : ""}{impact.score_effect.provisional_delta})
                {" · "}Verified GMARI {impact.score_effect.verified_before} → {impact.score_effect.verified_after}
                {" "}({impact.score_effect.verified_delta >= 0 ? "+" : ""}{impact.score_effect.verified_delta})
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="overline-label text-slate-400">Reports affected</p>
                <p className="text-slate-700 mt-0.5">{impact.reports_affected.stored_report_versions} stored report version(s). {impact.reports_affected.note}</p>
              </div>
              <div>
                <p className="overline-label text-slate-400">Related records</p>
                <p data-testid="archive-related" className="text-slate-700 mt-0.5">
                  {impact.related_records.citations} citation(s) · {impact.related_records.design_corrections} design correction(s)
                  {impact.related_records.substudies.length > 0 && ` · substudies: ${impact.related_records.substudies.join(", ")}`}
                  {impact.related_records.source_fact_id && " · linked extraction fact"}
                </p>
              </div>
            </div>
            <div>
              <p className="overline-label text-slate-400 mb-1">Reason (required)</p>
              <textarea data-testid="archive-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this evidence being archived?"
                className="w-full text-xs border border-slate-300 px-2.5 py-2 min-h-[64px]" />
            </div>
            <p className="text-[10px] text-slate-400">Soft delete — the record stays recoverable for {impact.recovery_days} days, and the full audit history is preserved.</p>
            <div className="flex gap-2">
              <button data-testid="archive-confirm" disabled={busy} onClick={submit}
                className="flex-1 bg-red-700 text-white text-xs font-semibold py-2.5 hover:bg-red-800 disabled:opacity-50">
                {busy ? "Archiving…" : "Confirm archive"}
              </button>
              <button onClick={onClose} className="px-4 text-xs text-slate-500 border border-slate-300 hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const ArchivedAssets = ({ productId, onChanged }) => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    axios.get(`${API}/evidence/${productId}/archived/list`)
      .then((r) => { setD(r.data); setError(false); })
      .catch(() => setError(true));
  }, [productId]);

  useEffect(() => { setD(null); load(); }, [load]);

  const restore = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/evidence/${productId}/${id}/restore`);
      toast.success("Evidence restored — all calculations updated");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section data-testid="archived-assets" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200">
        <p className="font-heading font-bold text-sm tracking-tight">Archived Evidence</p>
        <p className="text-xs text-slate-500 mt-1">Soft-deleted records with a 30-day recovery window. Archived evidence is excluded from every score, gap, dossier and report until restored.</p>
      </div>
      {error ? (
        <p data-testid="archived-error" className="px-5 py-6 text-center text-xs text-red-600">Could not load archived evidence. <button onClick={load} className="underline">Retry</button></p>
      ) : !d ? (
        <p data-testid="archived-loading" className="px-5 py-6 text-center text-xs text-slate-400"><CircleNotch size={14} className="animate-spin inline mr-1.5" />Loading archive…</p>
      ) : d.archived.length === 0 ? (
        <p data-testid="archived-empty" className="px-5 py-6 text-center text-xs text-slate-400">No archived evidence.</p>
      ) : (
        d.archived.map((a) => (
          <div key={a.id} data-testid={`archived-row-${a.id}`} className="px-5 py-3 border-b border-slate-100 last:border-0 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-700 truncate">{a.name}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Archived {a.archived_at?.slice(0, 10)} by {a.archived_by} · "{a.archive_reason}"
                {a.days_remaining !== null && (a.restorable
                  ? ` · ${a.days_remaining} day(s) left to restore`
                  : " · recovery period expired")}
              </p>
            </div>
            {isAdmin && a.restorable && (
              <button data-testid={`restore-asset-${a.id}`} disabled={busy === a.id} onClick={() => restore(a.id)}
                className="text-[11px] font-semibold px-3 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1 disabled:opacity-50">
                <ArrowCounterClockwise size={12} /> {busy === a.id ? "Restoring…" : "Restore"}
              </button>
            )}
          </div>
        ))
      )}
    </section>
  );
};
